import React, { useState } from "react";
import { Box, Typography, IconButton, Divider } from "@mui/material";
import { Repeat, Timer, X, ChevronRight } from "lucide-react";
import makeStyles from "@mui/styles/makeStyles";
import clsx from "clsx";
import { colorSystem } from "../../theme";
import { Brain, BicepsFlexed, Frown, Smile, Clock } from "lucide-react";
import { FEEL_MAP } from "../training-summary/TrainingSummaryWellBegin";

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: colorSystem.blue5,
    borderRadius: theme.spacing(1.5),
    padding: theme.spacing(2),
    cursor: "pointer",
    transition: "all 0.2s ease",
    width: "100%",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  titleBlock: {
    display: "flex",
    flexDirection: "column",
  },
  divide: {
    marginTop: theme.spacing(1.5),
  },
  footer: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.5),
  },
  footerItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    padding: theme.spacing(0.5, 1),
    backgroundColor: theme.palette.common.white,
    color: theme.palette.text.secondary,
    borderRadius: theme.spacing(2.5),
    fontSize: "0.75rem",
    fontWeight: 500,
  },
  icon: {
    width: 16,
    height: 16,
    color: theme.palette.text.secondary,
  },
  actionButton: {
    color: theme.palette.primary.main,
    padding: theme.spacing(0.5),
    width: 32,
    height: 32,
  },
  mindIconColor: {
    color: theme.palette.primary.main,
  },
  bodyIconColor: {
    color: colorSystem.blue1,
  },
  feelIconColor: {
    color: theme.palette.activity.habit,
  },
  timeIconColor: {
    color: theme.palette.common.black,
  },
}));

export type WorkoutCardBaseProps = {
  title: string;
  subtitle?: React.ReactNode;
  onClick?: () => void;
  type?: "arrow" | "close" | "none";
  showDivider?: boolean;
  onClose?: () => void;
  footerStats?: {
    mind?: string;
    body?: string;
    feel?: string;
    time?: number | null;
  };
  footerActions?: React.ReactNode;
  className?: string;
  sx?: object;
};

const WorkoutCardBase: React.FC<WorkoutCardBaseProps> = ({
  title,
  subtitle,
  onClick,
  type = "arrow",
  showDivider = true,
  onClose,
  footerStats,
  footerActions,
  className,
  sx,
}) => {
  const s = useStyles();

  const hasFooterStats =
    footerStats?.mind ||
    footerStats?.body ||
    footerStats?.feel ||
    footerStats?.time;

  const shouldShowDivider = hasFooterStats;
  const [open, setOpen] = useState(false);

  const toggleOpen = () => setOpen(!open);
  const styleAfter = (value: string) => {
    return FEEL_MAP[value]?.class ? s[FEEL_MAP[value].class] : "";
  };
  const FeelIcon = ({ value, ...props }) => {
    const Icon = FEEL_MAP[value]?.icon || Smile;
    return <Icon {...props} />;
  };

  return (
    <Box className={clsx(s.root, className)} sx={sx} onClick={onClick}>
      <Box className={s.header}>
        <Box className={s.titleBlock}>
          <Typography variant="subtitle1" fontWeight={600}>
            {title}
          </Typography>

          {subtitle && (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>

        {type === "arrow" && <ChevronRight className={s.actionButton} />}
        {type === "close" && (
          <IconButton
            className={s.actionButton}
            onClick={(e) => {
              e.stopPropagation();
              onClose?.();
            }}
          >
            <X size={24} strokeWidth={2} />
          </IconButton>
        )}
      </Box>

      {shouldShowDivider && <Divider className={s.divide} />}

      {hasFooterStats && (
        <Box className={s.footer}>
          {footerStats?.mind && (
            <Typography variant="body2" className={s.footerItem}>
              <Brain className={clsx(s.icon, s.mindIconColor)} />
              {footerStats.mind}
            </Typography>
          )}

          {footerStats?.body && (
            <Typography variant="body2" className={s.footerItem}>
              <BicepsFlexed className={clsx(s.icon, s.bodyIconColor)} />
              {footerStats.body}
            </Typography>
          )}

          {footerStats?.feel && (
            <Typography
              className={clsx(
                s.footerItem,

                styleAfter(footerStats.feel),
              )}
            >
              <FeelIcon
                value={footerStats.feel}
                className={clsx(s.icon, s.feelIconColor)}
              />
              {footerStats.feel}
            </Typography>
          )}

          {footerStats?.time && (
            <Typography variant="body2" className={s.footerItem}>
              <Clock className={clsx(s.icon, s.timeIconColor)} />
              {footerStats.time} min
            </Typography>
          )}
          {footerActions && <Box sx={{ ml: "auto" }}>{footerActions}</Box>}
        </Box>
      )}
    </Box>
  );
};

export default WorkoutCardBase;
